import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { formatMoney, formatWhen } from '../core/format';
import { errorMessage } from '../core/http-error';
import { LEAD_STATUSES, leadStatusLabel } from '../core/lead-status';
import { Agent, CrmNotice, Lead } from '../core/models';
import { NoticeBoard } from '../core/notice-board';

@Component({
  selector: 'app-crm-lead',
  templateUrl: './crm-lead.html',
})
export class CrmLeadPage implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly auth = inject(AuthService);
  protected readonly board = inject(NoticeBoard);
  protected readonly noticesOpen = signal(false);
  private readonly opened = new Set<number>();
  protected readonly when = formatWhen;
  protected readonly money = formatMoney;
  protected readonly statuses = LEAD_STATUSES;
  protected readonly leads = signal<Lead[]>([]);
  protected readonly agents = signal<Agent[]>([]);
  protected readonly failed = signal<string | null>(null);
  protected readonly status = signal('NEW');
  protected readonly assigneeId = signal('');
  protected readonly comment = signal('');
  protected readonly noteDraft = signal('');
  protected readonly savingStatus = signal(false);
  protected readonly savingNote = signal(false);
  protected readonly addingNote = signal(false);
  protected readonly mailing = signal(false);
  protected readonly sendingMail = signal(false);
  protected readonly mailSubject = signal('');
  protected readonly mailBody = signal('');
  protected readonly mailTemplate = signal('');
  protected readonly composerTitle = signal('Add comment');
  protected readonly loaded = signal(false);
  protected readonly tab = signal<'activity' | 'accounts' | 'transactions' | 'compliance' | 'profile' | 'comms'>('activity');
  protected readonly editingStatus = signal(false);
  protected readonly confirmingDelete = signal(false);
  protected readonly deleting = signal(false);
  protected readonly crediting = signal(false);
  protected readonly addingCredit = signal(false);
  protected readonly creditAmount = signal('');
  private readonly leadId = signal<number | null>(null);
  private timer = 0;

  protected readonly admin = computed(() => this.auth.user()?.role === 'ADMIN');
  protected readonly filtered = computed(() => this.applyFilters(this.leads()));
  protected readonly lead = computed(() => this.leads().find((item) => item.id === this.leadId()) ?? null);
  protected readonly notes = computed(() => [...(this.lead()?.comments ?? [])].reverse());
  protected readonly personal = computed(() => [...(this.lead()?.notes ?? [])].reverse());
  protected readonly place = computed(() => this.filtered().findIndex((item) => item.id === this.leadId()));
  protected readonly atStart = computed(() => this.place() <= 0);
  protected readonly atEnd = computed(() => {
    const index = this.place();
    return index < 0 || index >= this.filtered().length - 1;
  });

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.leadId.set(Number(params.get('id')));
      this.comment.set('');
      this.sync();
    });
    this.load();
    this.timer = window.setInterval(() => this.load(), 15000);
  }

  ngOnDestroy(): void {
    window.clearInterval(this.timer);
  }

  protected presence(person: Lead): string {
    if (person.online) {
      return 'online';
    }
    if (person.offlineMinutes == null) {
      return 'offline';
    }
    if (person.offlineMinutes < 1) {
      return 'offline < 1 min';
    }
    const minutes = person.offlineMinutes;
    return `offline ${minutes} min`;
  }

  protected back(): void {
    void this.router.navigate(['/crm'], { queryParams: this.route.snapshot.queryParams });
  }

  protected step(offset: number): void {
    const next = this.filtered()[this.place() + offset];
    if (!next) {
      return;
    }
    void this.router.navigate(['/crm/leads', next.id], { queryParamsHandling: 'preserve' });
  }

  protected openMail(): void {
    this.mailSubject.set('');
    this.mailBody.set('');
    this.mailTemplate.set('');
    this.mailing.set(true);
  }

  protected linkLabel(line: string): string {
    if (line.includes('/signup')) {
      return 'Open your account';
    }
    if (line.includes('/signin')) {
      return 'Sign in';
    }
    if (line.includes('apps.apple.com')) {
      return 'Download for iPhone';
    }
    if (line.includes('play.google.com')) {
      return 'Download for Android';
    }
    return 'Open link';
  }

  protected mailLines(): string[] {
    return this.mailBody().split('\n').map((line) => line.trim()).filter((line) => line.length > 0);
  }

  protected applyTemplate(value: string): void {
    this.mailTemplate.set(value);
    if (value === 'account') {
      this.mailSubject.set('Open your CapiTrade account');
      this.mailBody.set(
        'Hello,\n\nOpen your account on this page:\nhttps://capitrade.net/signup\n\nEnter your name, email, and password. The new account starts at $0. When it is open, sign in here:\nhttps://capitrade.net/signin\n\nCapiTrade\nsupport@capitrade.net',
      );
      return;
    }
    if (value === 'deposit') {
      this.mailSubject.set('How to make a deposit');
      this.mailBody.set(
        'Hello,\n\nSign in on this page:\nhttps://capitrade.net/signin\n\nAfter you are signed in, open Deposit. Choose the coin, copy the address shown, and send the amount from your wallet. The deposit is added after it is confirmed.\n\nCapiTrade\nsupport@capitrade.net',
      );
      return;
    }
    if (value === 'missed') {
      this.mailSubject.set('We tried to reach you');
      this.mailBody.set(
        'Hello,\n\nWe tried to call you, but the call was not answered.\n\nReply to this email when you are free, and we will call you again.\n\nCapiTrade\nsupport@capitrade.net',
      );
      return;
    }
    if (value === 'trust') {
      this.mailSubject.set('How to create a Trust Wallet');
      this.mailBody.set(
        'Hello,\n\nHere is how to create a Trust Wallet and add money with your card.\n\nimg:/mail/trust-step-1.jpg\nStep 1: Download the app on your phone.\nhttps://apps.apple.com/app/trust-crypto-bitcoin-wallet/id1288339409\nhttps://play.google.com/store/apps/details?id=com.wallet.crypto.trustapp\n\nimg:/mail/trust-step-2.jpg\nStep 2: Open Trust Wallet.\nOnce the download is complete, open the app on your phone.\n\nimg:/mail/trust-step-3.jpg\nStep 3: Create or import your wallet.\nFollow the instructions in the app to create a new wallet or import an existing wallet.\n\nimg:/mail/trust-step-4.jpg\nStep 4: Add balance to your wallet using your credit or debit card.\n\nCapiTrade\nsupport@capitrade.net',
      );
      return;
    }
    this.mailSubject.set('');
    this.mailBody.set('');
  }

  protected askCredit(): void {
    this.creditAmount.set('');
    this.crediting.set(true);
  }

  protected cancelCredit(): void {
    if (!this.addingCredit()) {
      this.crediting.set(false);
    }
  }

  protected confirmCredit(event?: Event): void {
    event?.preventDefault();
    const id = this.leadId();
    const amount = Math.round(Number(this.creditAmount()) * 100) / 100;
    if (!id || !Number.isFinite(amount) || amount < 0.01) {
      this.failed.set('Enter an amount of at least 0.01.');
      return;
    }
    this.addingCredit.set(true);
    this.failed.set(null);
    this.http.post(`/api/crm/leads/${id}/credit`, { amount: amount.toFixed(2) }).subscribe({
      next: () => {
        this.addingCredit.set(false);
        this.crediting.set(false);
        this.load();
      },
      error: (error: unknown) => {
        this.addingCredit.set(false);
        this.failed.set(errorMessage(error, 'Could not add that balance.'));
      },
    });
  }

  protected askDelete(): void {
    this.confirmingDelete.set(true);
  }

  protected cancelDelete(): void {
    if (!this.deleting()) {
      this.confirmingDelete.set(false);
    }
  }

  protected confirmDelete(event?: Event): void {
    event?.preventDefault();
    const id = this.leadId();
    if (!id) {
      return;
    }
    this.deleting.set(true);
    this.http.delete(`/api/crm/leads/${id}`).subscribe({
      next: () => {
        this.deleting.set(false);
        this.confirmingDelete.set(false);
        this.back();
      },
      error: (error: unknown) => {
        this.deleting.set(false);
        this.confirmingDelete.set(false);
        this.failed.set(errorMessage(error, 'Could not delete that lead.'));
      },
    });
  }

  protected sendMail(event?: Event): void {
    event?.preventDefault();
    const id = this.leadId();
    const subject = this.mailSubject().trim();
    const body = this.mailBody().trim();
    if (!id || !subject || !body) {
      this.failed.set('Add a subject and a message.');
      return;
    }
    this.sendingMail.set(true);
    this.http.post<Lead>(`/api/crm/leads/${id}/email`, { subject, body }).subscribe({
      next: () => {
        this.sendingMail.set(false);
        this.mailing.set(false);
        this.failed.set(null);
        this.load();
      },
      error: (error: unknown) => {
        this.sendingMail.set(false);
        this.failed.set(errorMessage(error, 'Could not send that email.'));
      },
    });
  }

  protected openNote(): void {
    this.editingStatus.set(false);
    this.noteDraft.set('');
    this.addingNote.set(true);
  }

  protected saveNote(event?: Event): void {
    event?.preventDefault();
    const id = this.leadId();
    const body = this.noteDraft().trim();
    if (!id || this.savingNote()) {
      return;
    }
    if (!body) {
      this.addingNote.set(false);
      return;
    }
    this.savingNote.set(true);
    this.http.post<Lead>(`/api/crm/leads/${id}/notes`, { body }).subscribe({
      next: () => {
        this.savingNote.set(false);
        this.addingNote.set(false);
        this.noteDraft.set('');
        this.load();
      },
      error: (error: unknown) => {
        this.savingNote.set(false);
        this.failed.set(errorMessage(error, 'Could not save the note.'));
      },
    });
  }

  protected openComposer(title: string): void {
    const person = this.lead();
    if (person) {
      this.status.set(person.status);
    }
    this.addingNote.set(false);
    this.comment.set('');
    this.composerTitle.set(title);
    this.editingStatus.set(true);
  }

  protected saveComposer(event?: Event): void {
    event?.preventDefault();
    const id = this.leadId();
    const person = this.lead();
    if (!id || !person || this.savingStatus()) {
      return;
    }
    const body = this.comment().trim();
    const statusChanged = this.status() !== person.status;
    if (!statusChanged && !body) {
      this.editingStatus.set(false);
      return;
    }
    this.savingStatus.set(true);
    const saveComment = () => {
      if (!body) {
        this.finishComposer();
        return;
      }
      this.http.post<Lead>(`/api/crm/leads/${id}/comments`, { body }).subscribe({
        next: () => this.finishComposer(),
        error: (error: unknown) => {
          this.savingStatus.set(false);
          this.failed.set(errorMessage(error, 'Could not save the comment.'));
        },
      });
    };
    if (!statusChanged) {
      saveComment();
      return;
    }
    this.http.post<Lead>(`/api/crm/leads/${id}/status`, { status: this.status() }).subscribe({
      next: () => saveComment(),
      error: (error: unknown) => {
        this.savingStatus.set(false);
        this.failed.set(errorMessage(error, 'Could not change the status.'));
      },
    });
  }

  protected assign(): void {
    const id = this.leadId();
    if (!id) {
      return;
    }
    this.http
      .post<Lead>(`/api/crm/leads/${id}/assign`, { assigneeId: this.assigneeId() ? Number(this.assigneeId()) : null })
      .subscribe({
        next: () => this.load(),
        error: (error: unknown) => this.failed.set(errorMessage(error, 'Could not assign the lead.')),
      });
  }

  protected callable(phone: string | null | undefined): boolean {
    return !!phone && phone !== '—' && /\d/.test(phone);
  }

  protected dial(phone: string): string {
    return phone.replace(/[^\d+]/g, '');
  }

  protected call(event: Event, phone: string): void {
    event.preventDefault();
    const number = this.dial(phone);
    if (!number) {
      return;
    }
    window.location.href = `sip:${number}`;
  }

  protected text(event: Event, phone: string): void {
    event.preventDefault();
    const number = this.dial(phone);
    if (!number) {
      return;
    }
    void navigator.clipboard?.writeText(number).catch(() => undefined);
    window.location.href = `sms:${number}`;
  }

  protected label(status: string): string {
    return leadStatusLabel(status);
  }

  private finishComposer(): void {
    this.savingStatus.set(false);
    this.editingStatus.set(false);
    this.comment.set('');
    this.tab.set('activity');
    this.load();
  }

  protected stamp(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private load(): void {
    this.http.get<Lead[]>('/api/crm/leads').subscribe({
      next: (rows) => {
        this.leads.set(rows);
        this.loaded.set(true);
        this.failed.set(null);
        this.sync();
        this.board.refresh();
      },
      error: (error: unknown) => this.failed.set(errorMessage(error, 'Could not load this lead.')),
    });
    if (this.auth.user()?.role === 'ADMIN' && this.agents().length === 0) {
      this.http.get<Agent[]>('/api/crm/agents').subscribe({
        next: (rows) => this.agents.set(rows),
        error: () => this.agents.set([]),
      });
    }
  }

  private sync(): void {
    const lead = this.leads().find((item) => item.id === this.leadId());
    if (!lead) {
      return;
    }
    if (!this.editingStatus()) {
      this.status.set(lead.status);
    }
    this.assigneeId.set(lead.assigneeId ? String(lead.assigneeId) : '');
    this.markOpened(lead);
  }

  protected openNotice(item: CrmNotice): void {
    this.board.acknowledge(item, (saved) => {
      if (!saved.openable || !saved.leadId || saved.leadId === this.leadId()) {
        return;
      }
      this.noticesOpen.set(false);
      void this.router.navigate(['/crm/leads', saved.leadId], { queryParamsHandling: 'preserve' });
    });
  }

  private markOpened(lead: Lead): void {
    if (lead.status !== 'NEW' || lead.seen || this.opened.has(lead.id)) {
      return;
    }
    this.opened.add(lead.id);
    this.http.post<Lead>(`/api/crm/leads/${lead.id}/seen`, {}).subscribe({
      next: (updated) => this.leads.update((rows) => rows.map((row) => (row.id === updated.id ? updated : row))),
      error: () => this.opened.delete(lead.id),
    });
  }

  private applyFilters(rows: Lead[]): Lead[] {
    const params = this.route.snapshot.queryParamMap;
    const term = (params.get('q') ?? '').trim().toLowerCase();
    const country = params.get('country') ?? '';
    const status = params.get('status') ?? '';
    const agent = params.get('agent') ?? '';
    return rows.filter((lead) => {
      if (country && lead.country !== country) {
        return false;
      }
      if (status && lead.status !== status) {
        return false;
      }
      if (agent === 'none' && lead.assigneeId != null) {
        return false;
      }
      if (agent && agent !== 'none' && String(lead.assigneeId ?? '') !== agent) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [lead.firstName, lead.lastName, lead.country, lead.email, lead.phone, lead.status, lead.assigneeName]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }
}
